import { useQuery } from '@tanstack/react-query';

import { TEAM_TASKS_VIEW_TYPES } from 'constants/ProjectConstants';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import {
  getInitiativeStats,
  getInitiativesTableData,
  getInitiativesCardsData,
  getDuration,
  getMyInitiativesTableData,
} from 'services/task';

const getFilters = (dateRange, filters, loggedInEmployee = null, addNameFilter = false) => {
  const duration = getDuration({
    startTS: dateRange.startDate,
    endTS: dateRange.endDate,
    durationKey: 'LASTYEAR',
  });

  const isAllDateRange = !dateRange?.startDate && !dateRange?.endDate;

  const payloadFilter = [
    {
      type: 'TYPE',
      operatorType: 'IN',
      data: ['TASK'],
    },
    {
      type: 'TASK_TYPE',
      operatorType: 'IN',
      data: ['SUB_TASK', 'TASK'],
    },
    {
      type: 'STATUS',
      operatorType: 'IN',
      data: filters.toString() === 'ALL' ? ['ONGOING', 'DELAYED', 'COMPLETED', 'UNPLANNED'] : filters,
    },
  ];

  if (!isAllDateRange) {
    payloadFilter.push({
      type: 'ACTIVE_CONTENT',
      operatorType: 'GREATER_THAN',
      data: [duration?.startTS],
    });
    payloadFilter.push({
      type: 'ACTIVE_CONTENT',
      operatorType: 'LESS_THAN',
      data: [duration?.endTS],
    });
  }

  const updatedFilter = addNameFilter
    ? [
        ...payloadFilter,
        {
          type: 'NAME',
          operatorType: 'IN',
          data: [loggedInEmployee?.id],
        },
      ]
    : payloadFilter;

  return updatedFilter;
};

const getData = async ({ cohort, dateRange, filters }) => {
  try {
    const res = await getInitiativeStats({
      cohort,
      dateRange,
      filters,
    });
    return res?.entity;
  } catch (err) {
    console.error('Error fetching  data : ', err);
  }
};

const getTableData = async ({ cohort, dateRange, resourceType, attributeFilter }) => {
  try {
    const res = await getInitiativesTableData({
      cohort,
      dateRange,
      resourceType,
      attributeFilter,
    });
    return res;
  } catch (err) {
    console.error('Error fetching  data : ', err);
  }
};

const getMyInitiativeTableData = async ({ type, dateRange }) => {
  try {
    const res = await getMyInitiativesTableData({
      type,
      dateRange,
    });
    return res;
  } catch (err) {
    console.error('Error fetching my initiatives data : ', err);
  }
};

// ref: https://stackoverflow.com/questions/64830935/react-query-refetch-query-only-if-the-state-variable-is-changed
export const useInitiativeTableData = ({
  cohort,
  dateRange,
  resourceType = TEAM_TASKS_VIEW_TYPES.list,
  attributeFilter,
  enabled,
}) =>
  useQuery(
    [REACT_QUERY_KEYS.InitiativeTableData, cohort, dateRange, attributeFilter],
    () =>
      getTableData({
        cohort,
        dateRange,
        resourceType,
        attributeFilter,
      }),
    { enabled: !!cohort && enabled, refetchOnWindowFocus: false }
  );

export const useMyInitiativeTableData = ({ type, enabled, dateRange }) =>
  useQuery(
    [REACT_QUERY_KEYS.MyInitiativeTableData, type, dateRange],
    () =>
      getMyInitiativeTableData({
        type,
        dateRange,
      }),
    { enabled, refetchOnWindowFocus: false }
  );

const getCardsData = async ({ cohort, dateRange, filters }) => {
  try {
    const res = await getInitiativesCardsData({
      cohort,
      dateRange,
      filters,
      limit: 4,
      offset: 0,
    });
    return res;
  } catch (err) {
    console.error('Error fetching  data : ', err);
  }
};

export const useInitiativeCardsData = ({ cohort, dateRange, filters }) =>
  useQuery([REACT_QUERY_KEYS.InitiativeCardsData, cohort, dateRange], () =>
    getCardsData({
      cohort,
      filters: getFilters(dateRange, filters),
      dateRange,
    })
  );
